import React, { useState } from 'react'
import { Button, Typography, Space, Alert, Divider, Spin } from 'antd'
import {
  LinkOutlined,
  CloudUploadOutlined,
  EyeOutlined,
  LoadingOutlined,
  CheckCircleOutlined
} from '@ant-design/icons'
import { useDispatch, useSelector } from 'react-redux'
import { useParams } from 'react-router'
import PdfDropzone from '../../../components/PdfDropzone'
import styled, { keyframes } from 'styled-components'
import { createCourseCerti } from '../../../reducers/courseReducer'

const { Title, Text } = Typography

// Animations
const pulse = keyframes`
  0% { opacity: 1; }
  50% { opacity: 0.5; }
  100% { opacity: 1; }
`

const shimmer = keyframes`
  0% { background-position: -200% 0; }
  100% { background-position: 200% 0; }
`

export const VideoWrapper = styled.div`
  position: relative;
  padding-bottom: 56.25%;
`

export const VideoFrame = styled.iframe`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
`

const Container = styled.div`
  background: #ffffff;
  border-radius: 12px;
  padding: 24px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
`

const LinkSection = styled.div`
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  border-radius: 10px;
  padding: 20px;
  margin-bottom: 16px;
`

const ExternalLink = styled.a`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 12px 24px;
  background: #ffffff;
  color: #667eea;
  border-radius: 8px;
  text-decoration: none;
  font-weight: 600;
  font-size: 15px;
  transition: all 0.3s ease;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
    color: #764ba2;
  }
`

const CertificateSection = styled.div`
  background: #fafafa;
  border-radius: 10px;
  padding: 20px;
  border: 1px solid #f0f0f0;
`

const SmallDropzoneWrapper = styled.div`
  position: relative;
  
  .dropzone-container {
    min-height: 120px !important;
    padding: 16px !important;
  }
`

// Loading Overlay Styles
const LoadingOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(255, 255, 255, 0.9);
  backdrop-filter: blur(2px);
  border-radius: 10px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  z-index: 10;
  border: 2px dashed #1890ff;
`

const LoadingContent = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
`

const LoadingText = styled.div`
  font-size: 14px;
  font-weight: 500;
  color: #1890ff;
  animation: ${pulse} 1.5s ease-in-out infinite;
`

const ProgressBar = styled.div`
  width: 200px;
  height: 6px;
  background: #e6f7ff;
  border-radius: 3px;
  overflow: hidden;
  position: relative;

  &::after {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    height: 100%;
    width: 100%;
    background: linear-gradient(
      90deg,
      transparent,
      #1890ff,
      transparent
    );
    background-size: 200% 100%;
    animation: ${shimmer} 1.5s infinite;
  }
`

const UploadIcon = styled.div`
  width: 50px;
  height: 50px;
  border-radius: 50%;
  background: linear-gradient(135deg, #1890ff 0%, #096dd9 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 22px;
`

const CertificateLink = styled.a`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  color: #1890ff;
  font-weight: 500;
  text-decoration: none;
  padding: 6px 12px;
  background: #e6f7ff;
  border-radius: 6px;
  transition: all 0.2s ease;

  &:hover {
    background: #bae7ff;
    color: #096dd9;
  }
`

// Success Message Component
const SuccessOverlay = styled(LoadingOverlay)`
  background: rgba(246, 255, 237, 0.95);
  border: 2px dashed #52c41a;
`

const SuccessIcon = styled(UploadIcon)`
  background: linear-gradient(135deg, #52c41a 0%, #389e0d 100%);
`

const Video = ({ selectedLecture }) => {
  const [certificate, setCertificate] = useState(null)
  const [loading, setLoading] = useState(false)
  const [uploadSuccess, setUploadSuccess] = useState(false)
  const { courseId } = useParams()
  const dispatch = useDispatch()

  const course = useSelector((state) =>
    state.courses?.data?.find((course) => course.id === courseId)
  )

  const myEnrollment = course?.myEnrollment
  const certificateUrl = myEnrollment?.certificateUrl

  const handleUploadCertificate = async () => {
    if (!certificate) return

    setLoading(true)
    setUploadSuccess(false)

    const formData = new FormData()
    formData.append('file', certificate)
    formData.append('obj', JSON.stringify({}))

    try {
      await dispatch(createCourseCerti(courseId, formData))
      setUploadSuccess(true)
      setCertificate(null)
      
      // Hide success message after 2 seconds
      setTimeout(() => {
        setUploadSuccess(false)
      }, 2000)
    } catch (error) {
      console.error('Certificate upload failed:', error)
    } finally {
      setLoading(false)
    }
  }

  if (!selectedLecture) return null

  // YouTube embed
  if (selectedLecture.provider === 'youtube' && selectedLecture.videoId) {
    return (
      <VideoWrapper>
        <VideoFrame
          title={selectedLecture.title}
          src={`https://www.youtube.com/embed/${selectedLecture.videoId}`}
          frameBorder="0"
          allow="autoplay; clipboard-write; encrypted-media; picture-in-picture"
          allowFullScreen
        />
      </VideoWrapper>
    )
  }

  return (
    <Container>
      {/* Link Section */}
      <LinkSection>
        <Title level={5} style={{ color: '#fff', margin: 0, marginBottom: 8 }}>
          📺 External Course Content
        </Title>
        <Text
          style={{
            color: 'rgba(255,255,255,0.85)',
            display: 'block',
            marginBottom: 16
          }}
        >
          This lecture content is hosted externally. Click below to access it.
        </Text>

        <ExternalLink
          href={selectedLecture.url}
          target="_blank"
          rel="noopener noreferrer"
        >
          <LinkOutlined style={{ fontSize: 18 }} />
          Open Link
        </ExternalLink>
      </LinkSection>

      {/* Note below link */}
      <Alert
        message="Important"
        description="The course will be marked as completed only after you finish all lectures and upload a valid completion certificate."
        type="info"
        showIcon
        style={{ marginBottom: 20, borderRadius: 8 }}
      />

      <Divider style={{ margin: '16px 0' }} />

      {/* Certificate Upload Section */}
      <CertificateSection>
        <Space direction="vertical" size="middle" style={{ width: '100%' }}>
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center'
            }}
          >
            <Title level={5} style={{ margin: 0 }}>
              <CloudUploadOutlined
                style={{ marginRight: 8, color: '#1890ff' }}
              />
              Upload Certificate
            </Title>

            {certificateUrl && (
              <CertificateLink
                href={certificateUrl}
                target="_blank"
                rel="noopener noreferrer"
              >
                <EyeOutlined />
                View Uploaded Certificate
              </CertificateLink>
            )}
          </div>

          {/* Dropzone with Loading Overlay */}
          <SmallDropzoneWrapper>
            <PdfDropzone
              file={certificate}
              setFile={setCertificate}
              compact={true}
              disabled={loading}
            />
            
            {/* Loading State Overlay */}
            {loading && (
              <LoadingOverlay>
                <LoadingContent>
                  <UploadIcon>
                    <LoadingOutlined spin />
                  </UploadIcon>
                  <LoadingText>Uploading Certificate...</LoadingText>
                  <ProgressBar />
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    Please wait, do not close this page
                  </Text>
                </LoadingContent>
              </LoadingOverlay>
            )}

            {/* Success State Overlay */}
            {uploadSuccess && (
              <SuccessOverlay>
                <LoadingContent>
                  <SuccessIcon>
                    <CheckCircleOutlined />
                  </SuccessIcon>
                  <Text style={{ color: '#52c41a', fontWeight: 600, fontSize: 16 }}>
                    Certificate Uploaded Successfully!
                  </Text>
                </LoadingContent>
              </SuccessOverlay>
            )}
          </SmallDropzoneWrapper>

          <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
            <Button
              type="primary"
              size="middle"
              icon={<CloudUploadOutlined />}
              disabled={!certificate || loading}
              loading={loading}
              onClick={handleUploadCertificate}
              style={{
                borderRadius: 6,
                height: 40,
                paddingInline: 24
              }}
            >
              {certificateUrl ? 'Update Certificate' : 'Upload Certificate'}
            </Button>
          </div>
        </Space>
      </CertificateSection>
    </Container>
  )
}

export default Video